
/**
 * Utility to generate QR codes
 */

import { useState, useEffect } from 'react';

// Generate QR code data URL for a given URL
export const generateQRCode = async (url: string): Promise<string> => {
  if (!url) return '';
  
  try {
    // Use dynamic import to load the QRious library only when needed
    const QRious = (await import('qrious')).default;
    
    // Create an instance of QRious
    const qr = new QRious({
      value: url,
      size: 300,
      level: 'H', // High error correction level
      background: 'white',
      foreground: 'black',
    });
    
    // Return the data URL for the QR code
    return qr.toDataURL();
  } catch (error) {
    console.error('Error generating QR code:', error);
    return '';
  }
};

// Custom hook that uses generateQRCode to create QR codes
export const useQRCode = (text: string): string => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  
  useEffect(() => {
    if (!text) {
      setQrCodeUrl('');
      return;
    }
    
    let isMounted = true;
    
    const generateCode = async () => {
      try {
        const dataUrl = await generateQRCode(text);
        if (isMounted) {
          setQrCodeUrl(dataUrl);
        }
      } catch (error) {
        console.error('Error in useQRCode hook:', error);
        if (isMounted) {
          setQrCodeUrl('');
        }
      }
    };
    
    generateCode();
    
    return () => {
      isMounted = false;
    };
  }, [text]);
  
  return qrCodeUrl;
};
